import { Link } from "react-router-dom";
import { MapPin, Star, BadgeCheck, ArrowUpRight, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";
import { fmtLocation } from "../lib/api";
import { useLang } from "../lib/i18n";

export default function ListingCard({ listing, large = false }) {
  const { t, tOr } = useLang();
  const l = listing;
  const img = l.images && l.images.length > 0 ? l.images[0] : null;
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className="h-full"
    >
      <Link
        to={`/listing/${l.slug}`}
        data-testid={`listing-card-${l.slug}`}
        className="group bg-white rounded-xl border border-[#E2E8F0] overflow-hidden hover:shadow-xl hover:border-gold/50 transition-all duration-300 flex flex-col h-full relative"
      >
        {l.featured && (
          <span className="absolute top-3 right-3 bg-gold text-navy text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-md z-10" data-testid={`featured-badge-${l.slug}`}>
            {t("common.featured")}
          </span>
        )}
        {l.sponsored && (
          <span className="absolute bottom-3 right-3 bg-navy/90 text-gold text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full z-10" data-testid={`sponsored-badge-${l.slug}`}>
            {t("common.sponsored")}
          </span>
        )}
        <div className={`relative overflow-hidden ${large ? "h-56 sm:h-72" : "h-44 sm:h-48"}`}>
          {img ? (
            <img
              src={img}
              alt={l.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />
          ) : (
            <div className="w-full h-full bg-navy-royal/10 flex items-center justify-center">
              <span className="font-serif italic text-2xl text-navy-royal/50">{tOr(`cat.${l.category}`, l.category_label)}</span>
            </div>
          )}
          <span className="absolute top-3 left-3 bg-navy/90 backdrop-blur-md text-gold text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full border border-gold/30">
            {tOr(`cat.${l.category}`, l.category_label)}
          </span>
          {l.logo && (
            <span className="absolute bottom-3 left-3 w-12 h-12 rounded-xl bg-white border border-[#E2E8F0] shadow-md overflow-hidden" data-testid={`logo-${l.slug}`}>
              <img src={l.logo} alt={`${l.name} logo`} className="w-full h-full object-cover" />
            </span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-navy/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-sans font-semibold tracking-tight text-navy group-hover:text-navy-royal transition-colors ${large ? "text-xl" : "text-lg"}`}>
              {l.name}
            </h3>
            {l.verified && <BadgeCheck className="w-4 h-4 text-jade shrink-0" data-testid={`verified-badge-${l.slug}`} />}
          </div>
          {fmtLocation(l) && (
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-stone2">
              <MapPin className="w-3.5 h-3.5 text-gold-dark" />
              {fmtLocation(l)}
            </p>
          )}
          {l.event_date && (
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-jade font-medium">
              <CalendarDays className="w-3.5 h-3.5" />
              {new Date(l.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </p>
          )}
          <p className="mt-2 text-sm text-stone2 leading-relaxed line-clamp-2 flex-1">{l.description}</p>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E2E8F0]">
            {l.rating_avg ? (
              <span className="flex items-center gap-1 text-sm font-semibold text-navy">
                <Star className="w-4 h-4 fill-gold text-gold" /> {l.rating_avg}
              </span>
            ) : l.price ? (
              <span className="font-sans font-bold text-navy-royal">{l.price}</span>
            ) : (
              <span />
            )}
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-royal group-hover:text-gold-dark transition-colors">
              {t("common.viewdetails")} <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
